import { Inject, ChangeDetectorRef, LOCALE_ID, Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { ResizeEvent } from 'angular-resizable-element';
import * as dateFns from 'date-fns';
import { CalendarEvent } from '../../utils/CalendarEvent';
import { Calendar } from '../../utils/Calendar';
import { CalendarEventTimesChangedEvent } from '../../utils/CalendarEventTimesChangedEvent';
import { DayViewHour } from '../../utils/day/DayViewHour';
import { DayView } from '../../utils/day/DayView';
import { DayViewEvent } from '../../utils/day/DayViewEvent';
import { DayViewEventResize } from '../../utils/day/DayViewEventResize';
import { CalendarUtilsService } from './../../utils/calendar-utils.service';
import { CalendarDragHelper } from './../../utils/CalendarDragHelper';
import { CalendarResizeHelper } from './../../utils/CalendarResizeHelper';
import { validateEvents } from './../../utils/isInside';
import { DayViewHourSegment } from '../../utils/day/DayViewHourSegment';
/**
 * @hidden
 */
const MINUTES_IN_HOUR = 60;
export class DayViewComponent implements OnInit, OnChanges, OnDestroy {
/**
 * The current view date
 */
viewDate: Date;
/**
 * An array of events to display on view
 */
events: CalendarEvent[] = [];
/**
 * An array of events to display on view
 */
calendars: Calendar[] = [];
/**
 * The number of segments in an hour. Must be <= 6
 */
hourSegments = 2;
/**
 * The height in pixels of each hour segment
 */
hourSegmentHeight = 30;
/**
 * The day start hours in 24 hour time. Must be 0-23
 */
dayStartHour = 0;
/**
 * The day start minutes. Must be 0-59
 */
dayStartMinute = 0;
/**
 * The day end hours in 24 hour time. Must be 0-23
 */
dayEndHour = 23;
/**
 * The day end minutes. Must be 0-59
 */
dayEndMinute = 59;
/**
 * The width in pixels of each event on the view
 */
eventWidth = 150;
/**
 * An observable that when emitted on will re-render the current view
 */
refresh: Subject<any>;
/**
 * The locale used to format dates
 */
locale: string;
/**
 * The grid size to snap resizing and dragging of events to
 */
eventSnapSize: number = this.hourSegmentHeight;
/**
 * Called when an event title is clicked
 */
eventClicked: EventEmitter<{ event: CalendarEvent }> = new EventEmitter<{
    event: CalendarEvent;
  }>();
/**
 * Called when an hour segment is clicked
 */
hourSegmentClicked: EventEmitter<{ date: Date }> = new EventEmitter<{
    date: Date;
  }>();
/**
 * Called when an event is resized or dragged and dropped
 */
eventTimesChanged: EventEmitter<
    CalendarEventTimesChangedEvent
  > = new EventEmitter<CalendarEventTimesChangedEvent>();
/**
 * An output that will be called before the view is rendered for the current day.
 * If you add the `cssClass` property to a segment it will add that class to the hour segment in the template
 */
beforeViewRender: EventEmitter<{ body: DayViewHour[] }> = new EventEmitter();
/**
 * @hidden
 */
hours: DayViewHour[] = [];
/**
 * @hidden
 */
view: DayView;
/**
 * @hidden
 */
width = 0;
/**
 * @hidden
 */
refreshSubscription: Subscription;
/**
 * @hidden
 */
currentResizes: Map<DayViewEvent, DayViewEventResize> = new Map();
/**
 * @hidden
 */
validateDrag: (args: any) => boolean;
/**
 * @hidden
 */
validateResize: (args: any) => boolean;
/**
 * @param {?} cdr
 * @param {?} utils
 * @param {?} locale
 */
constructor(private cdr: ChangeDetectorRef,
private utils: CalendarUtilsService,  locale: string) {
    this.locale = locale;
   }
/**
 * @return {?}
 */
ngOnInit() {
    if (this.refresh) {
      this.refreshSubscription = this.refresh.subscribe(() => {
        this.refreshAll();
        this.cdr.markForCheck();
      });
    }
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: any): void {
    if (
      changes.viewDate ||
      changes.dayStartHour ||
      changes.dayStartMinute ||
      changes.dayEndHour ||
      changes.dayEndMinute
    ) {
      this.refreshHourGrid();
    }

    if (changes.events) {
      validateEvents(this.events);
    }

    if (changes.calendars) {
      for (const /** @type {?} */ calendar of this.calendars) {
        validateEvents(calendar.events);
        this.refreshView();
      }
    }

    if (
      changes.viewDate ||
      changes.events ||
      changes.calendars ||
      changes.dayStartHour ||
      changes.dayStartMinute ||
      changes.dayEndHour ||
      changes.dayEndMinute ||
      changes.eventWidth
    ) {
      this.refreshView();
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }
/**
 * @param {?} dropEvent
 * @param {?} segment
 * @return {?}
 */
eventDropped(
    dropEvent: { dropData?: { event?: CalendarEvent } },
    segment: DayViewHourSegment
  ): void {
    if (dropEvent.dropData && dropEvent.dropData.event) {
      this.eventTimesChanged.emit({
        event: dropEvent.dropData.event,
        newStart: segment.date
      });
    }
  }
/**
 * @param {?} event
 * @param {?} resizeEvent
 * @param {?} dayViewContainer
 * @return {?}
 */
resizeStarted(
    event: DayViewEvent,
    resizeEvent: ResizeEvent,
    dayViewContainer: HTMLElement
  ): void {
    this.currentResizes.set(event, {
      originalTop: event.top,
      originalHeight: event.height,
      edge: typeof resizeEvent.edges.top !== 'undefined' ? 'top' : 'bottom'
    });
    const /** @type {?} */ resizeHelper: CalendarResizeHelper = new CalendarResizeHelper(
      dayViewContainer
    );
    this.validateResize = ({ rectangle }) =>
      resizeHelper.validateResize({ rectangle });
    this.cdr.markForCheck();
  }
/**
 * @param {?} event
 * @param {?} resizeEvent
 * @return {?}
 */
resizing(event: DayViewEvent, resizeEvent: ResizeEvent): void {
    const /** @type {?} */ currentResize: any = this.currentResizes.get(event);
    if (resizeEvent.edges.top) {
      event.top = currentResize.originalTop + +resizeEvent.edges.top;
      event.height = currentResize.originalHeight - +resizeEvent.edges.top;
    } else if (resizeEvent.edges.bottom) {
      event.height = currentResize.originalHeight + +resizeEvent.edges.bottom;
    }
  }
/**
 * @param {?} dayEvent
 * @return {?}
 */
resizeEnded(dayEvent: DayViewEvent): void {
    const /** @type {?} */ currentResize: any = this.currentResizes.get(dayEvent);

    let /** @type {?} */ pixelsMoved: number;
    if (currentResize.edge === 'top') {
      pixelsMoved = dayEvent.top - currentResize.originalTop;
    } else {
      pixelsMoved = dayEvent.height - currentResize.originalHeight;
    }

    dayEvent.top = currentResize.originalTop;
    dayEvent.height = currentResize.originalHeight;

    const /** @type {?} */ pixelAmountInMinutes: number =
      MINUTES_IN_HOUR / (this.hourSegments * this.hourSegmentHeight);
    const /** @type {?} */ minutesMoved: number = pixelsMoved * pixelAmountInMinutes;
    let /** @type {?} */ newStart: Date = dayEvent.event.start;
    let /** @type {?} */ newEnd: Date = dayEvent.event.end;
    if (currentResize.edge === 'top') {
      newStart = dateFns.addMinutes(newStart, minutesMoved);
    } else if (newEnd) {
      newEnd = dateFns.addMinutes(newEnd, minutesMoved);
    }

    this.eventTimesChanged.emit({ newStart, newEnd, event: dayEvent.event });
    this.currentResizes.delete(dayEvent);
  }
/**
 * @param {?} event
 * @param {?} dayViewContainer
 * @return {?}
 */
dragStart(event: HTMLElement, dayViewContainer: HTMLElement): void {
    const /** @type {?} */ dragHelper: CalendarDragHelper = new CalendarDragHelper(
      dayViewContainer,
      event
    );
    this.validateDrag = ({ x, y }) =>
      this.currentResizes.size === 0 && dragHelper.validateDrag({ x, y });
    this.cdr.markForCheck();
  }
/**
 * @param {?} dayEvent
 * @param {?} draggedInPixels
 * @return {?}
 */
eventDragged(dayEvent: DayViewEvent, draggedInPixels: number): void {
    const /** @type {?} */ pixelAmountInMinutes: number =
      MINUTES_IN_HOUR / (this.hourSegments * this.hourSegmentHeight);
    const /** @type {?} */ minutesMoved: number = draggedInPixels * pixelAmountInMinutes;
    const /** @type {?} */ newStart: Date = dateFns.addMinutes(dayEvent.event.start, minutesMoved);
    // possible issue
    let /** @type {?} */ newEnd: Date = new Date();
    if (dayEvent.event.end) {
      newEnd = dateFns.addMinutes(dayEvent.event.end, minutesMoved);
    }
    this.eventTimesChanged.emit({ newStart, newEnd, event: dayEvent.event });
  }
/**
 * @return {?}
 */
private refreshHourGrid(): void {
    this.hours = this.utils.getDayViewHourGrid({
      viewDate: this.viewDate,
      hourSegments: this.hourSegments,
      dayStart: {
        hour: this.dayStartHour,
        minute: this.dayStartMinute
      },
      dayEnd: {
        hour: this.dayEndHour,
        minute: this.dayEndMinute
      }
    });
    this.beforeViewRender.emit({
      body: this.hours
    });
  }
/**
 * @return {?}
 */
private refreshView(): void {
    this.view = this.utils.getDayView({
      events: this.events,
      calendars: this.calendars,
      viewDate: this.viewDate,
      hourSegments: this.hourSegments,
      dayStart: {
        hour: this.dayStartHour,
        minute: this.dayStartMinute
      },
      dayEnd: {
        hour: this.dayEndHour,
        minute: this.dayEndMinute
      },
      eventWidth: this.eventWidth,
      segmentHeight: this.hourSegmentHeight
    });
  }
/**
 * @return {?}
 */
private refreshAll(): void {
    this.refreshHourGrid();
    this.refreshView();
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'calendar-day-view',
  template: `
    <div class="cal-day-view" #dayViewContainer>
      <div class="cal-day-view-header">
        <span class="date-ee">{{ viewDate | date:'EE' }}</span> <br/>
        <span class="date-d">{{ viewDate | date:'d' }}</span>
      </div>
      <calendar-day-view-all-day-event
        *ngFor="let event of view.allDayEvents"
        [event]="event"
        (eventClicked)="eventClicked.emit({event: event})">
      </calendar-day-view-all-day-event>
      <div class="cal-hour-rows">
        <div class="cal-events">
          <div
            #event
            *ngFor="let dayEvent of view?.events"
            class="cal-event-container"
            [class.cal-draggable]="dayEvent.event.draggable"
            [class.cal-starts-within-day]="!dayEvent.startsBeforeDay"
            [class.cal-ends-within-day]="!dayEvent.endsAfterDay"
            [ngClass]="dayEvent.event.cssClass"
            mwlResizable
            [resizeEdges]="{top: dayEvent.event?.resizable?.beforeStart, bottom: dayEvent.event?.resizable?.afterEnd}"
            [resizeSnapGrid]="{top: eventSnapSize, bottom: eventSnapSize}"
            [validateResize]="validateResize"
            (resizeStart)="resizeStarted(dayEvent, $event, dayViewContainer)"
            (resizing)="resizing(dayEvent, $event)"
            (resizeEnd)="resizeEnded(dayEvent)"
            mwlDraggable
            [dragAxis]="{x: false, y: dayEvent.event.draggable && currentResizes.size === 0}"
            [dragSnapGrid]="{y: eventSnapSize}"
            [validateDrag]="validateDrag"
            (dragStart)="dragStart(event, dayViewContainer)"
            (dragEnd)="eventDragged(dayEvent, $event.y)"
            [style.marginTop.px]="dayEvent.top"
            [style.height.px]="dayEvent.height"
            [style.marginLeft.px]="dayEvent.left + 70"
            [style.width.px]="dayEvent.width - 1">
            <calendar-day-view-event
              [dayEvent]="dayEvent"
              (eventClicked)="eventClicked.emit({event: dayEvent.event})">
            </calendar-day-view-event>
          </div>
        </div>
        <div class="cal-hour" *ngFor="let hour of hours" [style.minWidth.px]="view?.width + 70">
          <calendar-day-view-hour-segment
            *ngFor="let segment of hour.segments"
            [style.height.px]="hourSegmentHeight"
            [segment]="segment"
            [segmentHeight]="hourSegmentHeight"
            [locale]="locale"
            (click)="hourSegmentClicked.emit({date: segment.date})">
          </calendar-day-view-hour-segment>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .cal-day-view {
      background-color: white;
    }
    .cal-day-view .cal-hour-rows {
      width: 100%;
      border: solid 1px #EBEEF2;
      overflow-x: scroll;
      position: relative;
    }
  
    .cal-day-view .cal-hour {
      background-color: white;
    }
  
    .cal-event-container {
      position: absolute;
    }

    .cal-day-view-header {
      margin-left: 70px;
      border: solid 1px #EBEEF2;
      border-bottom: none !important;
      padding-left: 15px;
    }

    .date-ee {
      font-size: 14px;
    }
    .date-d {
      font-weight: 900;
      font-size: 20px;
    }
    calendar-day-view-event {
      cursor: pointer;
    }
  
  `]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
{type: CalendarUtilsService, },
{type: undefined, decorators: [{ type: Inject, args: [LOCALE_ID, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'viewDate': [{ type: Input },],
'events': [{ type: Input },],
'calendars': [{ type: Input },],
'hourSegments': [{ type: Input },],
'hourSegmentHeight': [{ type: Input },],
'dayStartHour': [{ type: Input },],
'dayStartMinute': [{ type: Input },],
'dayEndHour': [{ type: Input },],
'dayEndMinute': [{ type: Input },],
'eventWidth': [{ type: Input },],
'refresh': [{ type: Input },],
'locale': [{ type: Input },],
'eventSnapSize': [{ type: Input },],
'eventClicked': [{ type: Output },],
'hourSegmentClicked': [{ type: Output },],
'eventTimesChanged': [{ type: Output },],
'beforeViewRender': [{ type: Output },],
};
}

function DayViewComponent_tsickle_Closure_declarations() {
/** @type {?} */
DayViewComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
DayViewComponent.ctorParameters;
/** @type {?} */
DayViewComponent.propDecorators;
/**
 * The current view date
 * @type {?}
 */
DayViewComponent.prototype.viewDate;
/**
 * An array of events to display on view
 * @type {?}
 */
DayViewComponent.prototype.events;
/**
 * An array of events to display on view
 * @type {?}
 */
DayViewComponent.prototype.calendars;
/**
 * The number of segments in an hour. Must be <= 6
 * @type {?}
 */
DayViewComponent.prototype.hourSegments;
/**
 * The height in pixels of each hour segment
 * @type {?}
 */
DayViewComponent.prototype.hourSegmentHeight;
/**
 * The day start hours in 24 hour time. Must be 0-23
 * @type {?}
 */
DayViewComponent.prototype.dayStartHour;
/**
 * The day start minutes. Must be 0-59
 * @type {?}
 */
DayViewComponent.prototype.dayStartMinute;
/**
 * The day end hours in 24 hour time. Must be 0-23
 * @type {?}
 */
DayViewComponent.prototype.dayEndHour;
/**
 * The day end minutes. Must be 0-59
 * @type {?}
 */
DayViewComponent.prototype.dayEndMinute;
/**
 * The width in pixels of each event on the view
 * @type {?}
 */
DayViewComponent.prototype.eventWidth;
/**
 * An observable that when emitted on will re-render the current view
 * @type {?}
 */
DayViewComponent.prototype.refresh;
/**
 * The locale used to format dates
 * @type {?}
 */
DayViewComponent.prototype.locale;
/**
 * The grid size to snap resizing and dragging of events to
 * @type {?}
 */
DayViewComponent.prototype.eventSnapSize;
/**
 * Called when an event title is clicked
 * @type {?}
 */
DayViewComponent.prototype.eventClicked;
/**
 * Called when an hour segment is clicked
 * @type {?}
 */
DayViewComponent.prototype.hourSegmentClicked;
/**
 * Called when an event is resized or dragged and dropped
 * @type {?}
 */
DayViewComponent.prototype.eventTimesChanged;
/**
 * An output that will be called before the view is rendered for the current day.
 * If you add the `cssClass` property to a segment it will add that class to the hour segment in the template
 * @type {?}
 */
DayViewComponent.prototype.beforeViewRender;
/**
 * @hidden
 * @type {?}
 */
DayViewComponent.prototype.hours;
/**
 * @hidden
 * @type {?}
 */
DayViewComponent.prototype.view;
/**
 * @hidden
 * @type {?}
 */
DayViewComponent.prototype.width;
/**
 * @hidden
 * @type {?}
 */
DayViewComponent.prototype.refreshSubscription;
/**
 * @hidden
 * @type {?}
 */
DayViewComponent.prototype.currentResizes;
/**
 * @hidden
 * @type {?}
 */
DayViewComponent.prototype.validateDrag;
/**
 * @hidden
 * @type {?}
 */
DayViewComponent.prototype.validateResize;
/** @type {?} */
DayViewComponent.prototype.cdr;
/** @type {?} */
DayViewComponent.prototype.utils;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
